import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  BadgeCheck,
  Ban,
  ClipboardPlus,
  Copy,
  Crown,
  Download,
  FilePenLine,
  FileText,
  KeyRound,
  LogOut,
  Mail,
  MessageSquareText,
  MoreHorizontal,
  Phone,
  PhoneForwarded,
  Plus,
  RefreshCw,
  RotateCcw,
  SearchCheck,
  Send,
  ShieldCheck,
  StickyNote,
  TicketCheck,
  Trash2,
  UserCog,
  UserRoundPen,
  WalletCards,
  ChevronDown,
  X,
} from 'lucide-react';
import DropdownMenu from './DropdownMenu';
import { api, dateTime, money } from '../lib/api';

const post = body => ({ method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) });
const normal = value => String(value || '').trim().replace(/\s+/g, ' ').toLowerCase();
const label = value => String(value || '').replaceAll('_', ' ');

function clickButton(text, root = document) {
  const wanted = normal(text);
  const buttons = [...root.querySelectorAll('button')];
  const exact = buttons.find(button => normal(button.textContent) === wanted);
  const partial = buttons.find(button => normal(button.textContent).includes(wanted));
  const target = exact || partial;
  if (!target) return false;
  target.click();
  return true;
}

function brandLabel(value) {
  const raw = String(value || 'Sousa Murray').trim();
  if (!raw) return 'Sousa Murray';
  if (!raw.includes('_')) return raw;
  return raw.toLowerCase().split('_').filter(Boolean).map(part => part[0]?.toUpperCase() + part.slice(1)).join(' ');
}

function planValue(row) {
  return `${row.product_id || ''}::${row.price_id || ''}`;
}

function planDescription(row) {
  const name = row.plan_name || row.product_name || row.product_code || 'Plan';
  if (String(row.billing_type || '').toLowerCase() === 'lifetime') {
    return `${name} · Lifetime · ${money(0, row.currency || 'GBP')}`;
  }
  const parts = [name];
  if (row.price_code) parts.push(row.price_code);
  if (row.amount_minor != null) parts.push(`${money(row.amount_minor, row.currency || 'GBP')}${row.recurring_interval ? ` / ${row.recurring_interval}` : ''}`);
  return parts.join(' · ');
}

export default function Customer360HeaderActions({ customerId }) {
  const navigate = useNavigate();
  const [target, setTarget] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [account, setAccount] = useState(null);
  const [lifetimeOpen, setLifetimeOpen] = useState(false);

  const refreshAccount = () => api(`/api/customers/${encodeURIComponent(customerId)}/account-control`).then(setAccount).catch(() => setAccount(null));

  useEffect(() => {
    let active = true;
    Promise.allSettled([
      api(`/api/customers/${encodeURIComponent(customerId)}`),
      api(`/api/customers/${encodeURIComponent(customerId)}/account-control`),
    ]).then(results => {
      if (!active) return;
      if (results[0].status === 'fulfilled') setCustomer(results[0].value?.customer || null);
      if (results[1].status === 'fulfilled') setAccount(results[1].value || null);
    });
    return () => { active = false; };
  }, [customerId]);

  useEffect(() => {
    const handler = () => refreshAccount();
    window.addEventListener('customer360-account-refresh', handler);
    return () => window.removeEventListener('customer360-account-refresh', handler);
  }, [customerId]);

  useEffect(() => {
    const install = () => {
      const found = document.querySelector('.c360-record-toolbar') || document.querySelector('.record-actions.customer-action-bar');
      if (found) {
        found.dataset.customer360MenuUpgraded = 'true';
        setTarget(found);
      }
    };
    install();
    const observer = new MutationObserver(install);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => {
      observer.disconnect();
      document.querySelector('.c360-record-toolbar')?.removeAttribute('data-customer360-menu-upgraded');
      document.querySelector('.record-actions.customer-action-bar')?.removeAttribute('data-customer360-menu-upgraded');
    };
  }, [customerId]);

  const recordRoot = () => document.querySelector('main.content') || document;
  const customerAction = text => () => clickButton(text, recordRoot());
  const openRecordViewThen = (view, text) => () => {
    window.location.hash = view;
    window.setTimeout(() => clickButton(text, recordRoot()), 80);
  };
  const workflowAction = text => () => clickButton(text, document.querySelector('.customer-workflow-dock') || document);
  const openBackOffice = () => {
    window.location.hash = 'back-office';
    window.setTimeout(() => window.dispatchEvent(new CustomEvent('customer360-focus-back-office-referral')), 100);
  };
  const openWhatsApp = () => {
    window.location.hash = 'communications';
    window.setTimeout(() => window.dispatchEvent(new CustomEvent('customer360-open-whatsapp-composer')), 160);
  };
  const openAccount = () => window.dispatchEvent(new CustomEvent('customer360-open-id-modal', { detail: { tab: 'overview' } }));
  const accountCall = async (path, body, success) => {
    try {
      await api(`/api/customers/${encodeURIComponent(customerId)}/account-control/${path}`, post(body || {}));
      window.dispatchEvent(new CustomEvent('customer360-account-refresh'));
      await refreshAccount();
      window.alert(success);
    } catch (error) {
      window.alert(`Customer account action failed: ${error.message}`);
    }
  };
  const resetPassword = () => {
    if (!account?.identity?.registered) return;
    if (!window.confirm(`Send a password reset email to ${account.identity.user?.email || customer?.verified_email || 'this customer'}?`)) return;
    accountCall('password-reset', {}, 'Password reset email sent to the customer.');
  };
  const resendVerification = () => {
    if (!account?.identity?.registered) return;
    if (!window.confirm('Resend the Sousa Murray ID email verification message to this customer?')) return;
    accountCall('verification-email', {}, 'Verification email requested.');
  };
  const revokeSessions = () => {
    if (!account?.identity?.registered) return;
    const reason = window.prompt('Why are all Sousa Murray ID sessions being revoked? This will be audited.');
    if (!reason?.trim()) return;
    accountCall('sessions/revoke', { reason: reason.trim() }, 'Customer Sousa Murray ID sessions revoked.');
  };
  const toggleBlocked = () => {
    if (!account?.identity?.registered) return;
    const blocked = !account?.identity?.user?.blocked;
    const reason = window.prompt(`Why are you ${blocked ? 'blocking' : 'unblocking'} this Sousa Murray ID account? This will be audited.`);
    if (!reason?.trim()) return;
    accountCall('block', { blocked, reason: reason.trim() }, `Sousa Murray ID account ${blocked ? 'blocked' : 'unblocked'}.`);
  };

  const actionsGroups = [
    {
      label: 'Staff assistance',
      items: [
        { label: 'Add note', icon: StickyNote, onSelect: openRecordViewThen('internal', 'Add note') },
        { label: 'Create follow-up', icon: ClipboardPlus, onSelect: openRecordViewThen('internal', 'Create follow-up') },
        { label: 'Refer to Back Office', icon: UserCog, onSelect: openBackOffice },
        { label: 'Invite to services', icon: Send, onSelect: () => navigate('/service-invitations?customer=' + encodeURIComponent(customerId)) },
        { label: 'Manage lifetime plans', icon: Crown, onSelect: () => setLifetimeOpen(true) },
        { label: 'Review paid plans', icon: WalletCards, onSelect: () => navigate('/billing') },
      ],
    },
    {
      label: 'Sousa Murray ID & account',
      items: [
        { label: 'Open ID & Account', icon: KeyRound, onSelect: openAccount },
        { label: 'Reset password', icon: KeyRound, onSelect: resetPassword, disabled: !account?.identity?.registered },
        { label: 'Revoke sign-ins & sessions', icon: LogOut, onSelect: revokeSessions, disabled: !account?.identity?.registered },
        { label: 'Resend verification email', icon: BadgeCheck, onSelect: resendVerification, disabled: !account?.identity?.registered || account?.identity?.user?.emailVerified },
        { label: account?.identity?.user?.blocked ? 'Unblock Sousa Murray ID' : 'Block Sousa Murray ID', icon: Ban, danger: !account?.identity?.user?.blocked, onSelect: toggleBlocked, disabled: !account?.identity?.registered },
        { label: 'Investigate account', icon: SearchCheck, onSelect: openAccount },
      ],
    },
    {
      label: 'Customer service',
      items: [
        { label: 'Callback', icon: PhoneForwarded, onSelect: workflowAction('Callback') },
        { label: 'Create request', icon: TicketCheck, onSelect: () => navigate('/customers/' + encodeURIComponent(customerId) + '/requests/new?type=request') },
        { label: 'Record question / enquiry', icon: SearchCheck, onSelect: () => navigate('/customers/' + encodeURIComponent(customerId) + '/requests/new?type=question') },
        { label: 'Create follow-up task', icon: PhoneForwarded, onSelect: () => navigate('/customers/' + encodeURIComponent(customerId) + '/requests/new?type=task') },
        { label: 'Log service issue', icon: AlertTriangle, onSelect: () => navigate('/customers/' + encodeURIComponent(customerId) + '/requests/new?type=incident') },
        { label: 'Raise complaint', icon: AlertTriangle, onSelect: () => navigate('/customers/' + encodeURIComponent(customerId) + '/complaints/new') },
        { label: 'Refund request', icon: RotateCcw, onSelect: workflowAction('Refund') },
        { label: 'Billing dispute', icon: AlertTriangle, onSelect: workflowAction('Billing dispute') },
        { label: 'Data Protection / SAR', icon: ShieldCheck, onSelect: () => navigate('/data-protection?customer=' + encodeURIComponent(customerId)) },
      ],
    },
    {
      label: 'Customer record',
      items: [
        { label: 'Edit customer', icon: UserRoundPen, onSelect: customerAction('Edit customer') },
        { label: 'Add contact point', icon: Phone, onSelect: openRecordViewThen('customer', 'Add contact') },
        { label: 'Add address', icon: Plus, onSelect: openRecordViewThen('customer', 'Add address') },
        { label: 'Email customer', icon: Mail, onSelect: () => window.dispatchEvent(new CustomEvent('customer360-open-communication-composer', { detail: { channel: 'email' } })) },
        { label: 'WhatsApp customer', icon: MessageSquareText, onSelect: openWhatsApp },
        { label: 'Generate letter', icon: FileText, onSelect: () => window.dispatchEvent(new CustomEvent('customer360-open-communication-composer', { detail: { channel: 'letter' } })) },
      ],
    },
  ];

  const moreGroups = [
    {
      items: [
        { label: 'Edit details', icon: FilePenLine, onSelect: customerAction('Edit customer') },
        { label: 'Export customer data', icon: Download, onSelect: customerAction('Export') },
        {
          label: 'Copy customer number', icon: Copy, onSelect: async () => {
            if (customer?.customer_number) await navigator.clipboard?.writeText(String(customer.customer_number));
          },
          disabled: !customer?.customer_number,
        },
        { label: 'Refresh record', icon: RefreshCw, onSelect: customerAction('Refresh record') },
      ],
    },
    {
      items: [
        { label: 'Delete customer', icon: Trash2, danger: true, onSelect: customerAction('Delete') },
      ],
    },
  ];

  const controls = <div className="customer360-header-actions">
    <DropdownMenu
      ariaLabel="Customer360 actions"
      trigger={<><span>Actions</span><ChevronDown size={16} /></>}
      triggerClassName="button primary customer360-actions-trigger"
      groups={actionsGroups}
      width={280}
    />
    <DropdownMenu
      ariaLabel="More Customer360 actions"
      trigger={<MoreHorizontal size={18} />}
      triggerClassName="button secondary customer360-more-trigger"
      groups={moreGroups}
      width={230}
    />
  </div>;

  return <>
    {target && createPortal(controls, target)}
    {lifetimeOpen && <LifetimePlansModal customerId={customerId} close={() => setLifetimeOpen(false)} />}
  </>;
}

function LifetimePlansModal({ customerId, close }) {
  const [data, setData] = useState({ catalogue: [], entitlements: [], liveSubscriptions: [] });
  const [replaceExisting, setReplaceExisting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');
  const [service, setService] = useState('');
  const [selection, setSelection] = useState('');
  const [reason, setReason] = useState('');
  const [revoking, setRevoking] = useState(null);
  const [revocationReason, setRevocationReason] = useState('');

  const load = async () => {
    setLoading(true);
    setNotice('');
    try {
      const body = await api(`/api/customers/${encodeURIComponent(customerId)}/lifetime-entitlements`);
      setData({ catalogue: body.catalogue || [], entitlements: body.entitlements || [], liveSubscriptions: body.liveSubscriptions || [] });
    } catch (error) {
      setNotice(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [customerId]);

  const services = useMemo(() => {
    const map = new Map();
    for (const row of data.catalogue) {
      const key = row.brand_code || row.service_type || 'SOUSA_MURRAY';
      if (!map.has(key)) map.set(key, row.service_name || brandLabel(key));
    }
    return [...map.entries()].map(([value, text]) => ({ value, text }));
  }, [data.catalogue]);

  useEffect(() => {
    if (!services.length) return;
    if (!service || !services.some(item => item.value === service)) setService(services[0].value);
  }, [services, service]);

  const plans = useMemo(() => data.catalogue.filter(row => (row.brand_code || row.service_type || 'SOUSA_MURRAY') === service), [data.catalogue, service]);

  useEffect(() => {
    if (!plans.length) { setSelection(''); return; }
    if (!selection || !plans.some(row => planValue(row) === selection)) setSelection(planValue(plans[0]));
  }, [plans, selection]);

  const chosen = plans.find(row => planValue(row) === selection) || null;
  const currentLivePlan = (data.liveSubscriptions || []).find(row => String(row.brandCode || '').toUpperCase() === String(service || '').toUpperCase()) || null;
  const currentPlanIsSelectedLifetime = Boolean(
    currentLivePlan?.isLifetime
    && chosen
    && String(currentLivePlan.productCode || '').toUpperCase() === String(chosen.product_code || '').toUpperCase()
  );
  const replacementRequired = Boolean(currentLivePlan && !currentPlanIsSelectedLifetime);

  useEffect(() => { setReplaceExisting(false); }, [service, selection, currentLivePlan?.id]);

  const active = data.entitlements.filter(row => String(row.status || '').toLowerCase() === 'active');
  const history = data.entitlements.filter(row => String(row.status || '').toLowerCase() !== 'active');

  const grant = async event => {
    event.preventDefault();
    if (!chosen || !reason.trim()) return;
    if (replacementRequired && !replaceExisting) {
      setNotice('Error: This service already has a live Stripe plan. Confirm the replacement option before granting Lifetime access.');
      return;
    }
    setBusy(true);
    setNotice('');
    try {
      await api(`/api/customers/${encodeURIComponent(customerId)}/lifetime-entitlements`, post({
        product_id: chosen.product_id,
        price_id: chosen.price_id || null,
        reason: reason.trim(),
        replace_existing_subscription: replacementRequired && replaceExisting,
      }));
      setReason('');
      setReplaceExisting(false);
      await load();
      setNotice(replacementRequired ? 'Lifetime plan granted and the previous Stripe plan was replaced.' : currentPlanIsSelectedLifetime ? 'Existing Stripe Lifetime plan reconciled with Customer360.' : 'Lifetime plan granted.');
    } catch (error) {
      setNotice(`Error: ${error.message}`);
    } finally {
      setBusy(false);
    }
  };

  const revoke = async () => {
    if (!revoking || !revocationReason.trim()) return;
    setBusy(true);
    setNotice('');
    try {
      await api(`/api/customers/${encodeURIComponent(customerId)}/lifetime-entitlements/${encodeURIComponent(revoking.id)}/revoke`, post({ reason: revocationReason.trim() }));
      setRevoking(null);
      setRevocationReason('');
      await load();
      setNotice('Lifetime plan revoked.');
    } catch (error) {
      setNotice(`Error: ${error.message}`);
    } finally {
      setBusy(false);
    }
  };

  return <div className="modal-backdrop crm-modal" onMouseDown={event => event.target === event.currentTarget && !busy && close()}>
    <div className="crm-dialog lifetime-dialog">
      <div className="crm-dialog-head">
        <div><span className="dialog-eyebrow">Customer360 entitlement</span><h2 className="govuk-heading-m">Lifetime plans</h2><p className="govuk-body">Grant this customer lifetime access to one or more eligible Sousa Murray plans.</p></div>
        <button className="icon-button" type="button" onClick={close} disabled={busy}><X size={18} /></button>
      </div>
      <div className="crm-dialog-body lifetime-dialog-body">
        {notice && <div className={`operation-notice ${notice.startsWith('Error:') ? 'error' : ''}`}>{notice}</div>}
        <section className="lifetime-section">
          <div className="lifetime-section-heading"><div><strong>Active lifetime access</strong><span>{active.length} active plan{active.length === 1 ? '' : 's'} on this customer.</span></div></div>
          {loading ? <div className="empty-inline">Loading lifetime entitlements…</div> : active.length ? <div className="lifetime-entitlement-list">
            {active.map(row => <article className="lifetime-entitlement-card" key={`${row.source || 'current'}-${row.id}`}>
              <div><span className="lifetime-service">{row.service_name || brandLabel(row.brand_code)}</span><strong>{row.plan_name || label(row.price_code || row.product_code || row.plan_key)}</strong><small>{row.price_code || row.product_code || row.plan_key || 'Lifetime entitlement'} · granted {dateTime(row.granted_at)}</small></div>
              {row.legacy ? <span className="lifetime-legacy-badge">Legacy e-Signing</span> : <button className="button danger-outline small govuk-button govuk-button--secondary" type="button" onClick={() => { setRevoking(row); setRevocationReason(''); }}>Revoke</button>}
            </article>)}
          </div> : <div className="empty-inline">No active lifetime plans are recorded for this customer.</div>}
        </section>

        <form className="lifetime-section lifetime-grant-form" onSubmit={grant}>
          <div className="lifetime-section-heading"><div><strong>Grant another lifetime plan</strong><span>Lifetime access can be granted to more than one service or plan.</span></div><Crown size={18} /></div>
          <div className="crm-form-grid">
            <label className="crm-field govuk-label"><span>Service</span><select className="govuk-select" value={service} onChange={event => setService(event.target.value)} disabled={!services.length || busy}>{services.map(item => <option value={item.value} key={item.value}>{item.text}</option>)}</select></label>
            <label className="crm-field govuk-label"><span>Plan</span><select className="govuk-select" value={selection} onChange={event => setSelection(event.target.value)} disabled={!plans.length || busy}>{plans.map(row => <option value={planValue(row)} key={planValue(row)}>{planDescription(row)}</option>)}</select></label>
            {currentLivePlan && <div className="crm-field wide"><span>Current Stripe plan</span><div className={`operation-notice ${replacementRequired ? 'warning' : ''}`}><strong>{currentLivePlan.serviceName || brandLabel(currentLivePlan.brandCode)} — {currentLivePlan.planName || 'Current plan'}</strong><div>{currentPlanIsSelectedLifetime ? 'This is already the selected £0.00 Lifetime Stripe plan. Customer360 will reconcile the missing entitlement record rather than create a duplicate.' : `Status: ${label(currentLivePlan.status || 'active')} · ${currentLivePlan.isFree ? '£0.00 free plan' : money(currentLivePlan.amountMinor || 0, currentLivePlan.currency || 'GBP')}${currentLivePlan.interval ? ` / ${currentLivePlan.interval}` : ''}`}</div></div></div>}
            {replacementRequired && <label className="crm-field wide govuk-label"><span>Replace current Stripe plan</span><span style={{ display: 'flex', alignItems: 'flex-start', gap: 10, fontWeight: 400 }}><input type="checkbox" checked={replaceExisting} onChange={event => setReplaceExisting(event.target.checked)} disabled={busy} style={{ marginTop: 3 }} /><span>Replace the current Stripe plan with this £0.00 Lifetime plan. The Lifetime subscription is created first, then the existing subscription is cancelled without automatic refund or proration. This stops future recurring billing for the replaced plan.</span></span></label>}
            <label className="crm-field wide govuk-label"><span>Reason / authority</span><textarea className="govuk-textarea" rows="4" required value={reason} onChange={event => setReason(event.target.value)} placeholder="Record why lifetime access is being granted and who/what authorised it." /></label>
          </div>
          <div className="lifetime-form-footer"><span>{currentPlanIsSelectedLifetime ? 'This will reconcile the existing £0.00 Stripe Lifetime subscription with Customer360.' : replacementRequired ? 'A live Stripe plan already exists for this service. Confirm replacement to create the £0.00 Lifetime plan and cancel the current subscription safely.' : 'Granting creates a real £0.00 Stripe Lifetime subscription using the same CRM and Stripe billing path as normal plans.'}</span><button className="button primary govuk-button" disabled={busy || !selection || !reason.trim() || (replacementRequired && !replaceExisting)}>{busy ? 'Creating £0.00 Stripe plan…' : replacementRequired ? 'Replace with lifetime plan' : currentPlanIsSelectedLifetime ? 'Reconcile lifetime plan' : 'Grant lifetime plan'}</button></div>
        </form>

        {history.length > 0 && <section className="lifetime-section"><div className="lifetime-section-heading"><div><strong>Lifetime history</strong><span>Revoked and inactive entitlements remain auditable.</span></div></div><div className="lifetime-entitlement-list">{history.map(row => <article className="lifetime-entitlement-card muted" key={`${row.source || 'history'}-${row.id}`}><div><span className="lifetime-service">{row.service_name || brandLabel(row.brand_code)}</span><strong>{row.plan_name || label(row.price_code || row.product_code || row.plan_key)}</strong><small>{label(row.status)} · {row.revoked_at ? `revoked ${dateTime(row.revoked_at)}` : `updated ${dateTime(row.updated_at || row.granted_at)}`}</small></div></article>)}</div></section>}
      </div>
      <div className="crm-dialog-footer"><button className="button secondary govuk-button govuk-button--secondary" type="button" onClick={close} disabled={busy}>Close</button></div>
    </div>

    {revoking && <div className="nested-confirm-card" role="dialog" aria-modal="true" onMouseDown={event => event.stopPropagation()}>
      <div className="nested-confirm-head"><div><strong>Revoke lifetime plan</strong><span>{revoking.plan_name || revoking.price_code || revoking.product_code}</span></div><button className="icon-button" onClick={() => setRevoking(null)} disabled={busy}><X size={16} /></button></div>
      <label className="crm-field wide govuk-label"><span>Reason for revocation</span><textarea className="govuk-textarea" rows="4" value={revocationReason} onChange={event => setRevocationReason(event.target.value)} /></label>
      <div className="nested-confirm-actions"><button className="button secondary govuk-button govuk-button--secondary" onClick={() => setRevoking(null)} disabled={busy}>Cancel</button><button className="button danger-fill govuk-button govuk-button--warning" onClick={revoke} disabled={busy || !revocationReason.trim()}>{busy ? 'Revoking…' : 'Revoke lifetime access'}</button></div>
    </div>}
  </div>;
}
